import {
  Option,
  DateOption,
  DefaultDateOptionsColumn,
  DefaultPeriodOptionsColumn,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import getShipmentOptions from "../../../../../../../../../../components/Shipments/hooks/getShipmentOptions";
import { useAppState } from "../../../../../../../../../../context/StateContext";
import { Warehouse } from "../../../../../../../../../../models/warehouse";
import { ReportsShipmentsService } from "../../../../../../../../../../services/reports.shipments.service";
import ShipmentService from "../../../../../../../../../../services/shipment.service";
import WarehouseService from "../../../../../../../../../../services/warehouse.service";
import { CronJobType, ReportDateType, Status } from "../../../../../../../../../../util/enums";
import { useActionReportMaintenance } from "../../../../hooks/useAction";
import { FilterByOption } from "../../../../types";
import { convertValueToSearch } from "../../../../utils";

export const useShipmentsFilters = () => {
  const { t: translate } = useTranslation();
  const { getAllProjects } = useActionReportMaintenance();
  const { typeOptions, statusOptions, containerTypeOptions, crossDcokStatusOption } =
    getShipmentOptions();
  const { data: projects } = getAllProjects();
  const { isFMVersion, currentUser } = useAppState();

  const reportFilterByOptions: FilterByOption[] = [
    {
      name: translate("type"),
      column: "shipment_type",
      options: typeOptions,
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
    },
    {
      name: translate("status"),
      column: "status_shipment",
      options: statusOptions,
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
    },
    ...(isFMVersion
      ? [
          {
            name: translate("projectName"),
            column: "event_type",
            options: projects?.data?.rows?.map((project) => {
              return { label: project.name, value: project.name };
            }),
            getOptionLabel: (option: Option) => option?.label || "",
            optionColumn: "value",
            requiredMessage: translate("selectAtLeastOneProject"),
          },
          {
            name: translate("containerType"),
            column: "container_type",
            options: containerTypeOptions,
            getOptionLabel: (option: Option) => option?.label || "",
            optionColumn: "value",
          },
        ]
      : []),
    {
      column: "destination",
      getOptionLabel: (option: string) => option,
      name: translate("destination"),
      onSearch: (value: string) =>
        ReportsShipmentsService.getSuggestions({ column: "destination", options: value }),
      optionColumn: "name",
    },
  ];

  const crossDockFilterByOptions: FilterByOption[] = [
    {
      name: translate("projectName"),
      column: "project",
      options: projects?.data?.rows?.map((project) => {
        return { label: project.name, value: String(project.name) };
      }),
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
      requiredMessage: translate("selectAtLeastOneProject"),
    },
    {
      name: `${translate("container")} #`,
      column: "container",
      onSearch: (value: string) =>
        ShipmentService.getAll({
          page: 0,
          pageSize: 20,
          companyId: currentUser?.companyId,
          groupBy: JSON.stringify(["container_id"]),
          searchByOr: JSON.stringify(convertValueToSearch(value, "container_id")),
        }),
      getOptionLabel: (container: any) => container?.containerId,
      optionColumn: "containerId",
      requiredMessage: translate("selectAtLeastOneContainer"),
    },
    {
      name: `${translate("warehouse")} #`,
      column: "warehouse",
      onSearch: (value: string) =>
        WarehouseService.getAll({
          page: 0,
          pageSize: 30,
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          searchByOr: JSON.stringify(convertValueToSearch(value, "name")),
        }),
      getOptionLabel: (warehouse: Warehouse) => warehouse?.name ?? "",
      optionColumn: "name",
    },
    {
      name: translate("status"),
      column: "status",
      options: crossDcokStatusOption,
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
      multiple: false,
    },
  ];

  const crossDockPeriodOptions = [
    {
      name: "Custom date",
      column: DefaultPeriodOptionsColumn.CUSTOM_DATE,
      type: DefaultDateOptionsColumn.DATE,
    },
  ];

  const reportPeriodOptions = [
    {
      name: "Custom period",
      column: DefaultPeriodOptionsColumn.CUSTOM_PERIOD,
      type: DefaultDateOptionsColumn.DATE_RANGE,
    },
  ];
  // mudar o report
  const reportDateOptions: DateOption[] = [
    {
      column: ReportDateType.ETA_AT_DESTINATION,
      name: translate("etaAtDestination"),
    },
    {
      column: ReportDateType.ETA_WAREHOUSE,
      name: translate("estimateArrivalWH"),
    },
    {
      column: ReportDateType.ETA_UNPACK,
      name: translate("etaUnpackDate"),
    },
    {
      column: ReportDateType.ARRIVAL_AT_DESTINATION,
      name: translate("finalPortArrival"),
    },
    {
      column: "",
      name: translate("all"),
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
    },
  ];

  const crossDockDateOptions: DateOption[] = [
    {
      name: translate("date"),
      column: DefaultDateOptionsColumn.DATE,
      type: DefaultDateOptionsColumn.DATE,
    },
  ];

  const getShipmentReportFilters = (type: CronJobType) => {
    switch (type) {
      case CronJobType.REPORT:
        return reportFilterByOptions;
      case CronJobType.CROSS_DOCK_STATUS:
        return crossDockFilterByOptions;
      default:
        return reportFilterByOptions;
    }
  };

  const getShipmentReportDateOptions = (type: CronJobType) => {
    switch (type) {
      case CronJobType.REPORT:
        return reportDateOptions;
      case CronJobType.CROSS_DOCK_STATUS:
        return crossDockDateOptions;
      default:
        return reportDateOptions;
    }
  };

  const getShipmentReportPeriodOptions = (type: CronJobType) => {
    switch (type) {
      case CronJobType.CROSS_DOCK_STATUS:
        return crossDockPeriodOptions;
      case CronJobType.REPORT:
      default:
        return reportPeriodOptions;
    }
  };

  return { getShipmentReportFilters, getShipmentReportDateOptions, getShipmentReportPeriodOptions };
};
